import { AxiosError } from "axios";

export class APIError extends Error {
  readonly statusCode: number;
  readonly type: "CLIENT_ERROR" | "SERVER_ERROR";

  constructor(message: string, statusCode: number = 500) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.type = statusCode >= 400 && statusCode < 500 ? "CLIENT_ERROR" : "SERVER_ERROR";
  }

  static fromAxiosError(error: AxiosError): APIError {
    const { data, status } = error.response ?? {};
    return new APIError(extractHumanReadableMessage(data) || error.message, status || 500);
  }
}

// Backend wraps user-facing errors as ["message", ...], { data: ["message", ...] } or { message: "..." }.
// Unwrap them once here so every consumer of APIError.message gets a clean string.
const extractHumanReadableMessage = (data: unknown): string | undefined => {
  if (data == null) return undefined;
  if (typeof data === "string") return unwrapJsonString(data) ?? data;
  if (Array.isArray(data)) return unwrapArray(data);
  if (typeof data === "object") return unwrapObject(data as Record<string, unknown>);
  return undefined;
};

const unwrapJsonString = (raw: string): string | undefined => {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return unwrapArray(parsed);
    if (parsed && typeof parsed === "object") {
      return unwrapObject(parsed as Record<string, unknown>);
    }
  } catch {
    // Not JSON — caller falls back to the raw string.
  }
  return undefined;
};

const unwrapArray = (arr: unknown[]): string | undefined => {
  const messages = arr.filter((entry): entry is string => typeof entry === "string");
  return messages.length > 0 ? messages.join(", ") : undefined;
};

const unwrapObject = (obj: Record<string, unknown>): string | undefined => {
  if (Array.isArray(obj.data) && obj.data.length > 0) {
    return unwrapArray(obj.data);
  }
  if (typeof obj.message === "string") return obj.message;
  return JSON.stringify(obj);
};
